import { ampli } from "../../../../ampli";
import {
  RowDataReducerState,
  RowData,
  SetupBaseRequestInfo,
  LandingPageOptions,
  RowDataReducerAction,
  ToastInfo,
  JsonSchema,
  DeletedObjectsList,
  SetupMode,
  ActiveAccountsReceivableRecordState,
  SaveRecordResponse,
} from "../../../../types";
import {
  TRACK_RECORD_DATA_STATE_KEYS,
  DEFAULT_TOAST,
  SCHEMA_TITLES,
  ROW_DATA_KEYS,
} from "../../constants";
import {
  prepareSaveRecordData,
  prepareARScheduleTypeConversionRequest,
  isNewRecord,
  getUpdatedRowDataIfTabsExist,
  canSaveRecord,
  getSchemaTitles,
  getFieldsWithUITitles,
  handleSuccessCallbackActions,
  getUpdatedARConversionRowData,
} from "../../helpers";
import {
  clearRowDataErrorsForNonClonedRecords,
  handleRecordChangesAndValidationsReset,
} from "../../validations";

// ---------------------------------------------------------------------------
// Shared handler context — avoids passing 15+ params to every handler
// ---------------------------------------------------------------------------

export interface HeaderHandlerContext {
  schema: JsonSchema;
  rowData: RowData;
  rowDataList: RowData[];
  rowDataReducerState: RowDataReducerState;
  resetState: RowDataReducerState["resetState"];
  deletedObjectsList: DeletedObjectsList;
  setupName: string;
  setupMode: SetupMode;
  setupBaseRequestInfo: SetupBaseRequestInfo;
  selected: LandingPageOptions;
  selectedTabSectionKey: string | undefined;
  selectedDashboardTabSectionKey: string | undefined;
  dispatch: React.Dispatch<RowDataReducerAction>;
  // UI state setters
  setIsRecordOpen: (open: boolean) => void;
  setIsSideSheetOpen: (open: boolean) => void;
  setRecordPageToast: (toast: ToastInfo) => void;
  setLandingPageToast: (toast: ToastInfo) => void;
  setSelectedTabSectionKey: (key: string | undefined) => void;
  setActiveTabIndex: (index: number) => void;
  setCurrentPage: (page: number) => void;
  setSearchValue: (value: string) => void;
  // Mutation triggers
  saveSingleRecord: (request: ReturnType<typeof prepareSaveRecordData>) => void;
  convertARScheduleType: (
    request: ReturnType<typeof prepareARScheduleTypeConversionRequest>,
  ) => void;
  refetchRecordData: () => void;
  refetchRowDataList: () => void;
  resetSaveRequestId: () => void;
  activeAccountsReceivableRecordMigrationInfo: ActiveAccountsReceivableRecordState;
  setActiveAccountsReceivableRecordMigrationInfo: (
    info: ActiveAccountsReceivableRecordState,
  ) => void;
  resetConvertARScheduleTypeId: () => void;
}

// ---------------------------------------------------------------------------
// determineAction
// ---------------------------------------------------------------------------

export function determineAction(rowData: RowData): string {
  if (isNewRecord(rowData)) {
    return rowData[TRACK_RECORD_DATA_STATE_KEYS.TRACK_IS_CLONED_RECORD]
      ? "Copy New Entry"
      : "New Entry";
  }
  return "Edit Entry";
}

// ---------------------------------------------------------------------------
// handleCancelAndReset
// ---------------------------------------------------------------------------

export function handleCancelAndReset(ctx: HeaderHandlerContext): void {
  const preservedNewEntryRowData = ctx.resetState?.newEntryRowData ?? {};

  const updatedResetNewEntryRowData = getUpdatedRowDataIfTabsExist(
    preservedNewEntryRowData,
    ctx.selectedDashboardTabSectionKey,
  );

  ctx.dispatch({
    type: "UPDATE_ROW_DATA_REDUCER_STATE",
    payload: {
      schema: ctx.resetState?.schema,
      rowData: updatedResetNewEntryRowData,
      rowDataErrors: {},
      deletedObjectsList: {},
      isReadOnly: false,
    },
  });
  ctx.setRecordPageToast(DEFAULT_TOAST);
  ctx.setIsRecordOpen(false);
  ctx.setSelectedTabSectionKey(undefined);
  ctx.setActiveTabIndex(0);
}

// Close the side sheet instead of the full record page
export function closeSideSheet({
  setIsSideSheetOpen,
  setRecordPageToast,
  setLandingPageToast,
}: {
  setIsSideSheetOpen: (isOpen: boolean) => void;
  setRecordPageToast: (toast: ToastInfo) => void;
  setLandingPageToast: (toast: ToastInfo) => void;
}): void {
  setIsSideSheetOpen(false);
  setRecordPageToast(DEFAULT_TOAST);
  setLandingPageToast(DEFAULT_TOAST);
}

// ---------------------------------------------------------------------------
// handleNewEntryClick
// ---------------------------------------------------------------------------

export function handleNewEntryClick(ctx: HeaderHandlerContext): void {
  if (
    ctx.activeAccountsReceivableRecordMigrationInfo.gridTableToast.message
      .length > 0
  ) {
    ctx.setActiveAccountsReceivableRecordMigrationInfo({
      ...ctx.activeAccountsReceivableRecordMigrationInfo,
      isMigrationDialogOpen: true,
    });
    return;
  }
  const preservedResetNewEntryRowData = ctx.resetState?.newEntryRowData ?? {};

  const updatedResetNewEntryRowData = getUpdatedRowDataIfTabsExist(
    preservedResetNewEntryRowData,
    ctx.selectedDashboardTabSectionKey,
  );

  ctx.setLandingPageToast(DEFAULT_TOAST);
  if (
    ctx.setupMode === "NORMAL_SIDE_SHEET_SETUP" ||
    ctx.setupMode === "INVENTORY_BUILDINGS_SETUP"
  ) {
    ctx.setIsSideSheetOpen(true);
    ctx.setRecordPageToast(DEFAULT_TOAST);
  } else {
    ctx.setIsRecordOpen(true);
  }
  ctx.dispatch({
    type: "UPDATE_ROW_DATA_REDUCER_STATE",
    payload: {
      isReadOnly: false,
      rowData: updatedResetNewEntryRowData,
    },
  });

  ampli.newEntry({
    "Event Name": "New Entry",
    "Action Triggered": true,
  });

  clearRowDataErrorsForNonClonedRecords(
    ctx.dispatch,
    ctx.resetState?.newEntryRowData,
  );
}

// ---------------------------------------------------------------------------
// handleSave
// ---------------------------------------------------------------------------

export function handleSave(ctx: HeaderHandlerContext): void {
  ctx.resetSaveRequestId();

  const isValid = canSaveRecord({
    rowData: ctx.rowData,
    setupName: ctx.setupName,
    selectedTabSectionKey: ctx.selectedTabSectionKey,
    selectedDashboardTabSectionKey: ctx.selectedDashboardTabSectionKey,
    deletedObjectsList: ctx.deletedObjectsList,
    rowDataReducerState: ctx.rowDataReducerState,
    dispatch: ctx.dispatch,
    setRecordPageToast: ctx.setRecordPageToast,
    setLandingPageToast: ctx.setLandingPageToast,
  });

  if (!isValid) return;

  const schemaTitles = getSchemaTitles(ctx.schema);
  const fieldsWithTitles = getFieldsWithUITitles(ctx.rowData, schemaTitles);
  const action = determineAction(ctx.rowData);

  ampli.createRecord({
    "Save Record": true,
    Action: action,
    "Action Triggered": true,
    Store: ctx.selected.store.value,
    Application: ctx.selected.department.value,
    Setup: ctx.selected.setup.value,
    ...fieldsWithTitles,
  });

  const request = prepareSaveRecordData({
    rowData: ctx.rowData,
    setupName: ctx.setupName,
    selectedTabSectionKey: ctx.selectedTabSectionKey,
    selectedDashboardTabSectionKey: ctx.selectedDashboardTabSectionKey,
    setupBaseRequestInfo: ctx.setupBaseRequestInfo,
    deletedObjectsList: ctx.deletedObjectsList,
    setRecordPageToast: ctx.setRecordPageToast,
  });
  ctx.resetSaveRequestId();
  ctx.resetConvertARScheduleTypeId();
  ctx.saveSingleRecord(request);
  ctx.setCurrentPage(1);
  ctx.setSearchValue("");
}

// ---------------------------------------------------------------------------
// handleRecordReset
// ---------------------------------------------------------------------------

export function handleRecordReset(ctx: HeaderHandlerContext): void {
  handleRecordChangesAndValidationsReset({
    selectedTabSectionKey: ctx.selectedTabSectionKey,
    selectedDashboardTabSectionKey: ctx.selectedDashboardTabSectionKey,
    rowDataReducerState: ctx.rowDataReducerState,
    dispatch: ctx.dispatch,
  });

  ampli.clearChange({
    "Event Name": "Clear Changes",
    "Action Triggered": true,
  });

  ctx.setRecordPageToast(DEFAULT_TOAST);
}

// ---------------------------------------------------------------------------
// handleConvertARRecordScheduleType
// ---------------------------------------------------------------------------

export function handleConvertARRecordScheduleType(
  ctx: HeaderHandlerContext,
): void {
  const request = prepareARScheduleTypeConversionRequest(
    ctx.rowDataReducerState,
    ctx.rowData,
    ctx.selectedTabSectionKey,
    ctx.setupBaseRequestInfo,
    ctx.setRecordPageToast,
  );
  ctx.convertARScheduleType(request);
  ctx.setCurrentPage(1);
  ctx.setSearchValue("");
}

export const handleConversionARRecordDialogOpen = (
  ctx: HeaderHandlerContext,
): void => {
  const updatedARConversionRowData = getUpdatedARConversionRowData(
    ctx.rowData,
    ctx.rowDataList,
  );
  ctx.setActiveAccountsReceivableRecordMigrationInfo({
    ...ctx.activeAccountsReceivableRecordMigrationInfo,
    isMigrationDialogOpen: true,
    newARScheduleCode: updatedARConversionRowData[
      ROW_DATA_KEYS.NEW_SCHEDULE_CODE
    ] as string | null,
  });
};

// ---------------------------------------------------------------------------
// onSuccessCallback
//
// Only needs the subset of ctx used for cancel/reset and refetch — not
// the mutation triggers — so it can be built before useSaveRecord is called.
// ---------------------------------------------------------------------------

// TODO: Currently this is tightly coupled to the to SideSheetFooter and LandingPageHeader components. If we need to use this in other places, we should refactor it to be more generic.
export type SuccessCallbackContext = Omit<
  HeaderHandlerContext,
  | "saveSingleRecord"
  | "convertARScheduleType"
  | "activeAccountsReceivableRecordMigrationInfo"
  | "setActiveAccountsReceivableRecordMigrationInfo"
>;

export function buildOnSuccessCallback(ctx: SuccessCallbackContext) {
  return async (response: SaveRecordResponse): Promise<void> => {
    const { variant, message, resultObj } = response;

    ctx.setRecordPageToast({ variant, message });

    // Mirror non-success responses to the landing page toast for Inventory Buildings.
    if (
      ctx.setupName === SCHEMA_TITLES.INVENTORY_BUILDINGS &&
      response.variant !== "positive"
    ) {
      ctx.setLandingPageToast(response);
    }

    if (
      ctx.selectedTabSectionKey &&
      response.variant === "positive" &&
      !isNewRecord(ctx.rowData)
    ) {
      ctx.refetchRecordData();
      return;
    }

    if (response.variant === "positive") {
      await handleSuccessCallbackActions(ctx, resultObj);
      handleCancelAndReset(ctx as HeaderHandlerContext);
      ctx.setLandingPageToast(response);
      ctx.refetchRowDataList();
    }
  };
}
